import { EditorState, Compartment, EditorSelection, Transaction, type Extension } from "@codemirror/state";
import { EditorView, keymap, drawSelection, highlightActiveLine, lineNumbers } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentMore, indentLess, toggleLineComment, undo, redo, isolateHistory, selectParentSyntax } from "@codemirror/commands";
import { bracketMatching, foldGutter, indentOnInput, syntaxHighlighting, defaultHighlightStyle } from "@codemirror/language";
import { highlightSelectionMatches } from "@codemirror/search";
import type { KeymapProfile, LanguageId, MacroStepV1, RecordableCommandId, SearchAction, SearchSpec } from "./types";

export interface EditorCallbacks {
  onChange(text: string): void;
  onRecord(step: MacroStepV1): void;
  onPointerBlocked(): void;
  onOpenSearch(replace: boolean): void;
  onRecordToggle(): void;
  onPlay(): void;
  recording(): boolean;
  locked(): boolean;
}

export interface EditorController {
  view: EditorView;
  setDocument(text: string): void;
  setWrap(enabled: boolean): void;
  setProfile(profile: KeymapProfile): void;
  setLanguage(language: LanguageId): Promise<void>;
  command(id: RecordableCommandId, record?: boolean): boolean;
  search(action: SearchAction, spec: SearchSpec, record?: boolean): boolean;
  insert(text: string, record?: boolean): boolean;
  beginHistory(): void;
  endHistory(): void;
}

const languageCompartment = new Compartment();
const wrapCompartment = new Compartment();
const keymapCompartment = new Compartment();

async function languageExtension(language: LanguageId): Promise<Extension> {
  switch (language) {
    case "markdown": return (await import("@codemirror/lang-markdown")).markdown();
    case "json": return (await import("@codemirror/lang-json")).json();
    case "javascript": return (await import("@codemirror/lang-javascript")).javascript();
    case "typescript": return (await import("@codemirror/lang-javascript")).javascript({ typescript: true });
    case "python": return (await import("@codemirror/lang-python")).python();
    case "go": return (await import("@codemirror/lang-go")).go();
    case "html": return (await import("@codemirror/lang-html")).html();
    case "css": return (await import("@codemirror/lang-css")).css();
    case "yaml": return (await import("@codemirror/lang-yaml")).yaml();
    case "sql": return (await import("@codemirror/lang-sql")).sql();
    default: return [];
  }
}

function selectionMove(view: EditorView, id: RecordableCommandId): boolean {
  const range = view.state.selection.main;
  const line = view.state.doc.lineAt(range.head);
  const pos = range.head;
  let target = pos;
  if (id === "move-left" || id === "select-left") target = Math.max(0, pos - 1);
  else if (id === "move-right" || id === "select-right") target = Math.min(view.state.doc.length, pos + 1);
  else if (id === "move-up" || id === "move-down") {
    const nextNo = Math.min(view.state.doc.lines, Math.max(1, line.number + (id === "move-up" ? -1 : 1)));
    const next = view.state.doc.line(nextNo); target = Math.min(next.to, next.from + (pos - line.from));
  } else if (id === "line-start") target = line.from;
  else if (id === "line-end") target = line.to;
  else if (id === "doc-start") target = 0;
  else if (id === "doc-end") target = view.state.doc.length;
  else return false;
  const selecting = id.startsWith("select-");
  view.dispatch({ selection: selecting ? EditorSelection.single(range.anchor, target) : EditorSelection.cursor(target), scrollIntoView: true });
  return true;
}

function changeLine(view: EditorView, id: RecordableCommandId): boolean {
  const range = view.state.selection.main;
  const line = view.state.doc.lineAt(range.head);
  if (id === "delete-line") {
    const from = line.from, to = line.number < view.state.doc.lines ? line.to + 1 : line.from > 0 ? line.to : line.to;
    const deleteFrom = line.number === view.state.doc.lines && line.from > 0 ? line.from - 1 : from;
    view.dispatch({ changes: { from: deleteFrom, to }, selection: { anchor: deleteFrom }, scrollIntoView: true }); return true;
  }
  if (id === "duplicate-line") {
    const text = view.state.sliceDoc(line.from, line.to); const insert = line.number < view.state.doc.lines ? `${text}\n` : `\n${text}`;
    const at = line.number < view.state.doc.lines ? line.from : line.to;
    view.dispatch({ changes: { from: at, insert }, selection: { anchor: range.head + insert.length }, scrollIntoView: true }); return true;
  }
  if (id === "move-line-up" && line.number > 1) {
    const previous = view.state.doc.line(line.number - 1);
    const previousText = view.state.sliceDoc(previous.from, previous.to);
    const currentText = view.state.sliceDoc(line.from, line.to);
    const offset = previous.length + 1;
    view.dispatch({
      changes: { from: previous.from, to: line.to, insert: `${currentText}\n${previousText}` },
      selection: EditorSelection.single(range.anchor >= line.from && range.anchor <= line.to ? range.anchor - offset : range.head - offset, range.head - offset),
      scrollIntoView: true
    });
    return true;
  }
  if (id === "move-line-down" && line.number < view.state.doc.lines) {
    const next = view.state.doc.line(line.number + 1);
    const currentText = view.state.sliceDoc(line.from, line.to);
    const nextText = view.state.sliceDoc(next.from, next.to);
    const offset = next.length + 1;
    view.dispatch({
      changes: { from: line.from, to: next.to, insert: `${nextText}\n${currentText}` },
      selection: EditorSelection.single(range.anchor >= line.from && range.anchor <= line.to ? range.anchor + offset : range.head + offset, range.head + offset),
      scrollIntoView: true
    });
    return true;
  }
  if (id === "insert-line") { view.dispatch({ changes: { from: line.to, insert: "\n" }, selection: { anchor: line.to + 1 } }); return true; }
  if (id === "join-lines" && line.number < view.state.doc.lines) {
    const next = view.state.doc.line(line.number + 1); const leading = view.state.sliceDoc(next.from, next.to).match(/^\s*/)?.[0].length ?? 0;
    view.dispatch({ changes: { from: line.to, to: next.from + leading, insert: " " }, selection: { anchor: line.to + 1 } }); return true;
  }
  return false;
}

export function makeSearchRegExp(spec: SearchSpec): RegExp {
  const source = spec.regexp ? spec.query : spec.query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const wrapped = spec.wholeWord ? `\\b(?:${source})\\b` : source;
  return new RegExp(wrapped, `g${spec.caseSensitive ? "" : "i"}u`);
}

function findMatch(view: EditorView, spec: SearchSpec, backwards: boolean): { from: number; to: number; match: RegExpExecArray } | null {
  if (!spec.query) return null;
  let regex: RegExp; try { regex = makeSearchRegExp(spec); } catch { return null; }
  const text = view.state.doc.toString();
  if (backwards) {
    const before = view.state.selection.main.from; let found: { from: number; to: number; match: RegExpExecArray } | null = null; let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) && match.index < before) { found = { from: match.index, to: match.index + match[0].length, match }; if (!match[0]) regex.lastIndex++; }
    return found;
  }
  regex.lastIndex = view.state.selection.main.to;
  const match = regex.exec(text); return match ? { from: match.index, to: match.index + match[0].length, match } : null;
}

export function createEditor(parent: HTMLElement, callbacks: EditorCallbacks, initial = ""): EditorController {
  let suppressRecording = false;
  let selectionStack: EditorSelection[] = [];
  let currentLanguage: LanguageId = "text";
  let playbackHistory = false;
  let playbackStart: { doc: string; selection: EditorSelection } | null = null;
  let controller!: EditorController;
  const run = (id: RecordableCommandId) => controller.command(id, true);
  const bindings = (profile: KeymapProfile) => [
    { key: "Mod-f", run: () => (callbacks.onOpenSearch(false), true) },
    { key: "Mod-h", run: () => (callbacks.onOpenSearch(true), true) },
    { key: "F3", run: () => (callbacks.onOpenSearch(false), true) },
    { key: "Shift-F3", run: () => (callbacks.onOpenSearch(false), true) },
    { key: "Mod-d", run: () => run("duplicate-line") },
    { key: "Mod-Shift-k", run: () => run("delete-line") },
    { key: "Alt-ArrowUp", run: () => run("move-line-up") },
    { key: "Alt-ArrowDown", run: () => run("move-line-down") },
    { key: "Mod-/", run: () => run("toggle-line-comment") },
    { key: "Tab", run: () => run("indent-more") },
    { key: "Shift-Tab", run: () => run("indent-less") },
    { key: "F8", run: () => (callbacks.onRecordToggle(), true) },
    { key: "F9", run: () => (callbacks.onPlay(), true) },
    { key: "Alt-Shift-r", run: () => (callbacks.onRecordToggle(), true) },
    { key: "Alt-Shift-p", run: () => (callbacks.onPlay(), true) },
    { key: "Backspace", run: () => run("delete-backward") },
    { key: "Delete", run: () => run("delete-forward") },
    { key: "ArrowLeft", run: () => run("move-left") },
    { key: "ArrowRight", run: () => run("move-right") },
    { key: "ArrowUp", run: () => run("move-up") },
    { key: "ArrowDown", run: () => run("move-down") },
    { key: "Shift-ArrowLeft", run: () => run("select-left") },
    { key: "Shift-ArrowRight", run: () => run("select-right") },
    { key: "Home", run: () => run("line-start") },
    { key: "End", run: () => run("line-end") },
    { key: "Mod-Home", run: () => run("doc-start") },
    { key: "Mod-End", run: () => run("doc-end") },
    ...(profile === "jetbrains" ? [
      { key: "Mod-Shift-j", run: () => run("join-lines") },
      { key: "Mod-Shift-u", run: () => run("toggle-case") },
      { key: "Alt-Shift-ArrowRight", run: () => run("expand-selection") },
      { key: "Alt-Shift-ArrowLeft", run: () => run("shrink-selection") }
    ] : [])
  ];
  const state = EditorState.create({ doc: initial, extensions: [
    lineNumbers(), foldGutter(), history(), drawSelection(), highlightActiveLine(), bracketMatching(), indentOnInput(),
    syntaxHighlighting(defaultHighlightStyle, { fallback: true }), highlightSelectionMatches(),
    EditorState.allowMultipleSelections.of(false), languageCompartment.of([]), wrapCompartment.of([]),
    keymapCompartment.of(keymap.of([...bindings("standard"), ...historyKeymap, ...defaultKeymap])),
    EditorState.transactionExtender.of(() => playbackHistory ? { annotations: Transaction.addToHistory.of(false) } : null),
    EditorView.updateListener.of((update) => {
      if (!update.docChanged) return;
      callbacks.onChange(update.state.doc.toString());
      if (suppressRecording || !callbacks.recording()) return;
      update.changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => {
        if (toA > fromA) callbacks.onRecord({ kind: "command", command: "delete-forward" });
        const text = inserted.toString(); if (text) callbacks.onRecord({ kind: "insert", text });
      });
    }),
    EditorView.domEventHandlers({
      mousedown(event) { if (callbacks.recording()) { event.preventDefault(); callbacks.onPointerBlocked(); return true; } return callbacks.locked(); },
      beforeinput(event) { if (callbacks.locked()) { event.preventDefault(); return true; } return false; }
    }),
    EditorView.theme({ "&": { height: "min(58vh, 620px)" }, ".cm-scroller": { overflow: "auto" }, ".cm-content": { minHeight: "18rem" } })
  ] });
  const view = new EditorView({ state, parent });

  const command = (id: RecordableCommandId, record = false): boolean => {
    if (callbacks.locked() && record) return false;
    suppressRecording = true; let ok = false;
    try {
      if (selectionMove(view, id)) ok = true;
      else if (changeLine(view, id)) ok = true;
      else if (id === "indent-more") ok = indentMore(view);
      else if (id === "indent-less") ok = indentLess(view);
      else if (id === "toggle-line-comment") ok = toggleLineComment(view);
      else if (id === "delete-backward" || id === "delete-forward") {
        const range = view.state.selection.main; let from = range.from, to = range.to;
        if (range.empty) id === "delete-backward" ? from = Math.max(0, from - 1) : to = Math.min(view.state.doc.length, to + 1);
        if (from !== to) { view.dispatch({ changes: { from, to }, selection: { anchor: from } }); ok = true; }
      } else if (id === "toggle-case") {
        const range = view.state.selection.main; if (!range.empty) { const text = view.state.sliceDoc(range.from, range.to); const upper = text === text.toUpperCase() ? text.toLowerCase() : text.toUpperCase(); view.dispatch({ changes: { from: range.from, to: range.to, insert: upper }, selection: { anchor: range.from, head: range.from + upper.length } }); ok = true; }
      } else if (id === "expand-selection") {
        selectionStack.push(view.state.selection);
        if (currentLanguage !== "text") ok = selectParentSyntax(view);
        if (!ok) { const range = view.state.selection.main; const line = view.state.doc.lineAt(range.head); const text = view.state.sliceDoc(line.from, line.to); const left = text.length - text.trimStart().length; const right = text.trimEnd().length; const word = view.state.wordAt(range.head); const candidates = [word ? EditorSelection.single(word.from, word.to) : null, EditorSelection.single(line.from + left, line.from + right), EditorSelection.single(line.from, line.to), EditorSelection.single(0, view.state.doc.length)].filter((candidate): candidate is EditorSelection => candidate !== null); const next = candidates.find((candidate) => candidate.main.from < range.from || candidate.main.to > range.to); if (next) { view.dispatch({ selection: next }); ok = true; } }
      } else if (id === "shrink-selection") { const previous = selectionStack.pop(); if (previous) { view.dispatch({ selection: previous }); ok = true; } }
    } finally { suppressRecording = false; }
    if (ok && record) callbacks.onRecord({ kind: "command", command: id });
    if (!id.includes("selection")) selectionStack = [];
    return ok;
  };

  controller = {
    view,
    setDocument(text) { suppressRecording = true; view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text }, selection: { anchor: 0 } }); suppressRecording = false; },
    setWrap(enabled) { view.dispatch({ effects: wrapCompartment.reconfigure(enabled ? EditorView.lineWrapping : []) }); },
    setProfile(profile) { view.dispatch({ effects: keymapCompartment.reconfigure(keymap.of([...bindings(profile), ...historyKeymap, ...defaultKeymap])) }); },
    async setLanguage(language) { currentLanguage = language; view.dispatch({ effects: languageCompartment.reconfigure(await languageExtension(language)) }); },
    command,
    insert(text, record = false) { if (!text) return true; suppressRecording = true; const range = view.state.selection.main; view.dispatch({ changes: { from: range.from, to: range.to, insert: text }, selection: { anchor: range.from + text.length } }); suppressRecording = false; if (record) callbacks.onRecord({ kind: "insert", text }); return true; },
    search(action, spec, record = false) {
      if (!spec.query) return false;
      suppressRecording = true; let ok = false;
      try {
        if (action === "replace-all") {
          let regex: RegExp; try { regex = makeSearchRegExp(spec); } catch { return false; }
          const text = view.state.doc.toString(); const replacements: { from: number; to: number; insert: string }[] = []; let match: RegExpExecArray | null;
          while ((match = regex.exec(text))) { replacements.push({ from: match.index, to: match.index + match[0].length, insert: match[0].replace(regex, spec.replacement) }); if (!match[0]) regex.lastIndex++; }
          if (replacements.length) { view.dispatch({ changes: replacements }); ok = true; }
        } else {
          const backwards = action === "find-previous"; const found = findMatch(view, spec, backwards);
          if (found) { if (action === "replace-next") { const inserted = found.match[0].replace(makeSearchRegExp({ ...spec, wholeWord: false }), spec.replacement); view.dispatch({ changes: { from: found.from, to: found.to, insert: inserted }, selection: { anchor: found.from + inserted.length } }); } else view.dispatch({ selection: { anchor: found.from, head: found.to }, scrollIntoView: true }); ok = true; }
        }
      } finally { suppressRecording = false; }
      if (ok && record) callbacks.onRecord({ kind: "search", action, query: { ...spec } });
      return ok;
    },
    beginHistory() { playbackStart = { doc: view.state.doc.toString(), selection: view.state.selection }; playbackHistory = true; },
    endHistory() {
      if (!playbackStart) return;
      const start = playbackStart; const finalDoc = view.state.doc.toString(); const finalSelection = view.state.selection;
      playbackHistory = false; playbackStart = null; suppressRecording = true;
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: start.doc }, selection: start.selection, annotations: Transaction.addToHistory.of(false) });
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: finalDoc }, selection: finalSelection, annotations: isolateHistory.of("full") });
      suppressRecording = false;
    }
  };
  return controller;
}

export { undo, redo };
